import centroid from '@turf/centroid';
import { Popup } from 'maplibre-gl';
import { createElement, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import standsIcon from '../../assets/images/parking/stands-white.svg';
import wallLoopIcon from '../../assets/images/parking/wall-loop-white.svg';
import { ParkingDetailsPanel } from '../../components';
import { getParkingNetworkLabel, getParkingPointColor, isParkingOperated, isParkingSecure, parkingPointColors, parkingTypes } from '../../models';
import { PanoramaxService } from '../../services';
import { useCancellablePromise } from '../cancellable-promise';
import { useUnits } from '../units';
import { useLayers } from './layers';
import { useSource } from './source';
const areaSourceId = 'area';
const areaLayerId = 'area';
const cyclabilityZonesSourceId = 'cyclability-zones';
const cyclabilityZonesLayerId = 'cyclability-zones';
const cyclabilityZonesParkingLotsCountSourceId = 'cyclability-zones-parking-lots-count';
const cyclabilityZonesParkingLotsCountLayerId = 'cyclability-zones-parking-lots-count';
const cyclabilityZoneSourceId = 'cyclability-zone';
const cyclabilityZoneLayerId = 'cyclability-zone';
const highlightedZoneSourceId = 'highlighted-cyclability-zone';
const highlightedZoneLayerId = 'highlighted-cyclability-zone';
const parkingsSourceId = 'parkings';
const parkingsLayerId = 'parkings';
const highlightedParkingLayerId = 'highlighted-parking';
const highlightedSecureParkingLayerId = 'highlighted-parking-secure';
const parkingsV2SourceId = 'parkings-v2';
const parkingsV2LayerId = 'parkings-v2';
const parkingsV2HeatmapSourceId = 'parkings-v2-heatmap-tiles';
const parkingsV2SecurePointsLayerId = 'parkings-v2-secure-points';
const parkingsV2SecureClusterSourceId = 'parkings-v2-secure-clusters';
const parkingsV2OtherClusterSourceId = 'parkings-v2-other-clusters';
const parkingsV2SecureClusterLayerId = 'parkings-v2-secure-clusters';
const parkingsV2OtherClusterLayerId = 'parkings-v2-other-clusters';
const parkingsV2SecureClusterCountLayerId = 'parkings-v2-secure-clusters-count';
const parkingsV2OtherClusterCountLayerId = 'parkings-v2-other-clusters-count';
const parkingsV2ClusterLayerIds = [parkingsV2OtherClusterLayerId, parkingsV2SecureClusterLayerId];
const parkingsV2ClusterCountLayerIds = [parkingsV2OtherClusterCountLayerId, parkingsV2SecureClusterCountLayerId];
const legacyParkingsV2ClusterCountLayerId = 'parkings-v2-clusters-count';
const parkingsV2HeatmapLayerId = 'parkings-v2-heatmap';
const parkingHoverPanelCloseDelay = 800;

// The parking popups are built as HTML strings and some of their values come
// from OpenStreetMap free text (operator, network...): escape them.
function escapeHtml(value) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
const minParkingZoneCountRadius = 12;
const maxParkingZoneCountRadius = 30;
export const cyclabilityZonesColors = ['#a8caff', '#4c83d9', '#2762bd', '#0a429a', '#063072'];
const legacyParkingRadiuses = [{
  value: 4
}, {
  value: 5
}, {
  value: 6
}, {
  value: 7
}];
const parkingRadiuses = [{
  value: 8
}, {
  value: 10
}, {
  value: 11
}, {
  value: 12
}, {
  value: 13
}, {
  value: 14
}, {
  value: 15
}, {
  value: 16
}, {
  value: 18
}, {
  value: 20
}, {
  value: 25
}];
function getParkingZoneCountRadius(slots, maxSlots) {
  if (slots <= 0 || maxSlots <= 0) return 0;
  const ratio = Math.log1p(slots) / Math.log1p(maxSlots);
  return Math.max(minParkingZoneCountRadius, Math.round(ratio * maxParkingZoneCountRadius));
}
const parkingClusterProperties = {
  capacitySum: ['+', ['coalesce', ['get', 'capacity'], 0]],
  coveredCount: ['+', ['case', ['get', 'covered'], 1, 0]],
  freeCount: ['+', ['case', ['get', 'free'], 1, 0]],
  otherCapacity: ['+', ['case', ['!', ['get', 'secure']], ['coalesce', ['get', 'capacity'], 0], 0]],
  radius: ['max', ['get', 'radius']],
  secureCount: ['+', ['case', ['get', 'secure'], 1, 0]],
  secureCapacity: ['+', ['case', ['get', 'secure'], ['coalesce', ['get', 'capacity'], 0], 0]],
  operatorCount: ['+', ['case', ['get', 'operated'], 1, 0]]
};

// Capacity -> circle radius scale of the parking clusters, also used to build
// their legend.
export const parkingClusterRadiusStops = [{
  capacity: 0,
  radius: 9
}, {
  capacity: 50,
  radius: 12
}, {
  capacity: 200,
  radius: 18
}, {
  capacity: 1000,
  radius: 22
}];
const parkingClusterRadius = ['interpolate', ['linear'], ['get', 'capacitySum'], ...parkingClusterRadiusStops.flatMap(({
  capacity,
  radius
}) => [capacity, radius])];
export function useCyclabilityZones(map, _zoneMap, {
  onClick,
  getTooltipContent,
  onParkingDetailsAction,
  onParkingContributionSubmit,
  parkingHoverPanelPositionRef,
  parkingHoverPanelRef
}, {
  isParkings,
  darkTheme,
  outlineWidth,
  unselectedChildZoneOpacity,
  selectedZoneId,
  clusterParkings,
  parkingRenderMode,
  parkingClusterMaxZoom = 15,
  parkingDetailsMode = 'legacy',
  parkingHeatmapGeometry
}) {
  const [initialized, setInitialized] = useState(false);
  const resolvedParkingRenderMode = parkingRenderMode || (clusterParkings ? 'clusters' : 'points');
  const isParkingV2RenderMode = Boolean(parkingRenderMode || clusterParkings);
  const shouldClusterParkings = resolvedParkingRenderMode === 'clusters';
  const shouldRenderParkingHeatmap = resolvedParkingRenderMode === 'heatmap';
  const hasParkingPointInteractions = !shouldRenderParkingHeatmap;
  const activeParkingsSourceId = isParkingV2RenderMode ? parkingsV2SourceId : parkingsSourceId;
  const activeParkingsLayerId = isParkingV2RenderMode ? parkingsV2LayerId : parkingsLayerId;
  const lastRenderedChildZones = useRef(null);
  const {
    i18n,
    t
  } = useTranslation();
  const initializedRef = useRef(false);
  const highlightedZoneTooltip = useRef(null);
  const highlightedParkingTooltip = useRef(null);
  const parkingDetailsRoot = useRef(null);
  const parkingPanoramaxCache = useRef(new globalThis.Map());
  const parkingPanoramaxKey = useRef(null);
  const parkingHoverPanelCloseTimeout = useRef(undefined);
  const parkingHoverPanelHovered = useRef(false);
  const parkingHoverPanelPinned = useRef(false);
  const zoneMap = useRef(_zoneMap);
  const {
    addGeoJSONSource: addAreaSource,
    updateGeoJSONSource: updateAreaSource,
    clearGeoJSONSource: clearAreaSource
  } = useSource(map, areaSourceId);
  const {
    addGeoJSONSource: addCyclabilityZonesSource,
    updateGeoJSONSource: updateCyclabilityZonesSource,
    clearGeoJSONSource: clearCyclabilityZonesSource
  } = useSource(map, cyclabilityZonesSourceId);
  const {
    addGeoJSONSource: addCyclabilityZonesParkingLotsCountSource,
    updateGeoJSONSource: updateCyclabilityZonesParkingLotsCountSource,
    clearGeoJSONSource: clearCyclabilityZonesParkingLotsCountSource
  } = useSource(map, cyclabilityZonesParkingLotsCountLayerId);
  const {
    addGeoJSONSource: addCyclabilityZoneSource,
    getGeoJSONSource: getCyclabilityZoneSource,
    updateGeoJSONSource: updateCyclabilityZoneSource,
    clearGeoJSONSource: clearCyclabilityZoneSource
  } = useSource(map, cyclabilityZoneSourceId);
  const {
    addGeoJSONSource: addHighlightedZoneSource,
    getGeoJSONSource: getHighlightedZoneSource
  } = useSource(map, highlightedZoneSourceId);
  const {
    addGeoJSONSource: addParkingsSource,
    updateGeoJSONSource: updateParkingsSource,
    clearGeoJSONSource: clearParkingsSource
  } = useSource(map, activeParkingsSourceId);
  const {
    addGeoJSONSource: addSecureClusterParkingsSource,
    updateGeoJSONSource: updateSecureClusterParkingsSource,
    clearGeoJSONSource: clearSecureClusterParkingsSource
  } = useSource(map, parkingsV2SecureClusterSourceId);
  const {
    addGeoJSONSource: addOtherClusterParkingsSource,
    updateGeoJSONSource: updateOtherClusterParkingsSource,
    clearGeoJSONSource: clearOtherClusterParkingsSource
  } = useSource(map, parkingsV2OtherClusterSourceId);
  const {
    addGeoJSONSource: addParkingHeatmapSource,
    updateGeoJSONSource: updateParkingHeatmapSource,
    clearGeoJSONSource: clearParkingHeatmapSource
  } = useSource(map, parkingsV2HeatmapSourceId);
  const {
    addCircleLayer
  } = useLayers(map);
  const {
    formatNumber
  } = useUnits();
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  const selectedZoneIdRef = useRef(selectedZoneId);
  const onParkingContributionSubmitRef = useRef(onParkingContributionSubmit);
  const onParkingDetailsActionRef = useRef(onParkingDetailsAction);
  useEffect(() => {
    selectedZoneIdRef.current = selectedZoneId;
  }, [selectedZoneId]);
  useEffect(() => {
    onParkingContributionSubmitRef.current = onParkingContributionSubmit;
  }, [onParkingContributionSubmit]);
  useEffect(() => {
    onParkingDetailsActionRef.current = onParkingDetailsAction;
  }, [onParkingDetailsAction]);
  useEffect(() => {
    updateParkingHeatmapFilter();
  }, [parkingHeatmapGeometry, shouldRenderParkingHeatmap]);
  useEffect(() => {
    zoneMap.current = _zoneMap;
  }, [_zoneMap]);
  useEffect(() => {
    if (map) {
      map?.setPaintProperty(cyclabilityZonesLayerId, 'fill-outline-color', darkTheme ? '#d3d3d3' : '#3d3d3d');
      map?.setPaintProperty(highlightedZoneLayerId, 'line-color', darkTheme ? '#d3d3d3' : '#3d3d3d');
    }
  }, [darkTheme]);
  let hoveredZoneId;
  let hoveredParkingId;
  function init() {
    if (!map || initializedRef.current) return;
    highlightedZoneTooltip.current = new Popup({
      className: 'map-tooltip',
      closeButton: false
    });
    highlightedParkingTooltip.current = new Popup({
      className: 'map-tooltip',
      closeButton: false
    });
    if (getCyclabilityZoneSource()) {
      setupParkingSourceAndLayers();
      registerMapHandlers();
      initializedRef.current = true;
      setInitialized(true);
      return;
    }
    addAreaSource();
    addCyclabilityZonesSource();
    addCyclabilityZonesParkingLotsCountSource();
    addCyclabilityZoneSource();
    addHighlightedZoneSource();
    map.addLayer({
      id: cyclabilityZonesLayerId,
      type: 'fill',
      source: cyclabilityZonesSourceId,
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': ['coalesce', ['get', 'fillOpacity'], 0.5],
        'fill-outline-color': darkTheme ? '#fff' : '#3d3d3d'
      }
    }, 'labels');
    map.addLayer({
      id: `${cyclabilityZonesParkingLotsCountLayerId}-circle`,
      type: 'circle',
      source: cyclabilityZonesParkingLotsCountSourceId,
      paint: {
        'circle-radius': ['get', 'radius'],
        'circle-color': '#283859'
      }
    });
    map.addLayer({
      id: cyclabilityZonesParkingLotsCountLayerId,
      type: 'symbol',
      source: cyclabilityZonesParkingLotsCountSourceId,
      layout: {
        'text-field': ['get', 'count'],
        'text-font': ['Nunito'],
        'text-size': 12,
        'text-max-width': 3
      },
      paint: {
        'text-color': '#fff'
      }
    });
    map.addLayer({
      id: highlightedZoneLayerId,
      type: 'line',
      source: highlightedZoneSourceId,
      paint: {
        'line-color': darkTheme ? '#eee' : '#3d3d3d',
        'line-width': 2
      }
    }, 'labels');
    map.addLayer({
      id: cyclabilityZoneLayerId,
      type: 'line',
      source: cyclabilityZoneSourceId,
      paint: {
        'line-color': '#3d3d3d',
        'line-width': 2
      }
    }, 'labels');
    map.addLayer({
      id: areaLayerId,
      type: 'line',
      source: areaSourceId,
      paint: {
        'line-color': '#3d3d3d',
        'line-width': outlineWidth || 4
      }
    }, 'labels');
    setupParkingSourceAndLayers();
    registerMapHandlers();
    initializedRef.current = true;
    setInitialized(true);
  }
  function setupParkingSourceAndLayers() {
    if (!map) return;
    if (shouldClusterParkings) {
      const clusterSourceOptions = {
        type: 'geojson',
        cluster: true,
        clusterMaxZoom: parkingClusterMaxZoom,
        clusterRadius: 20,
        clusterProperties: parkingClusterProperties
      };
      addParkingsSource({
        type: 'FeatureCollection',
        features: []
      }, clusterSourceOptions);
      addOtherClusterParkingsSource({
        type: 'FeatureCollection',
        features: []
      }, clusterSourceOptions);
      addSecureClusterParkingsSource({
        type: 'FeatureCollection',
        features: []
      }, clusterSourceOptions);
      if (!map.getLayer(parkingsV2OtherClusterLayerId)) {
        map.addLayer({
          id: parkingsV2OtherClusterLayerId,
          type: 'circle',
          source: parkingsV2OtherClusterSourceId,
          paint: {
            'circle-color': parkingPointColors.other,
            'circle-radius': parkingClusterRadius,
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 3
          },
          filter: ['has', 'point_count']
        });
      }
      if (!map.getLayer(parkingsV2SecureClusterLayerId)) {
        map.addLayer({
          id: parkingsV2SecureClusterLayerId,
          type: 'circle',
          source: parkingsV2SecureClusterSourceId,
          paint: {
            'circle-color': parkingPointColors.secure,
            'circle-radius': parkingClusterRadius,
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 3
          },
          filter: ['has', 'point_count']
        });
      }
      if (!map.getLayer(parkingsV2OtherClusterCountLayerId)) {
        map.addLayer({
          id: parkingsV2OtherClusterCountLayerId,
          type: 'symbol',
          source: parkingsV2OtherClusterSourceId,
          layout: {
            'text-field': ['to-string', ['round', ['get', 'capacitySum']]],
            'text-font': ['Nunito'],
            'text-size': 12,
            'text-max-width': 4
          },
          paint: {
            'text-color': '#fff'
          },
          filter: ['has', 'point_count']
        });
      }
      if (!map.getLayer(parkingsV2SecureClusterCountLayerId)) {
        map.addLayer({
          id: parkingsV2SecureClusterCountLayerId,
          type: 'symbol',
          source: parkingsV2SecureClusterSourceId,
          layout: {
            'text-field': ['to-string', ['round', ['get', 'capacitySum']]],
            'text-font': ['Nunito'],
            'text-size': 12,
            'text-max-width': 4
          },
          paint: {
            'text-color': '#fff'
          },
          filter: ['has', 'point_count']
        });
      }
    } else if (!shouldRenderParkingHeatmap) {
      addParkingsSource();
    }
    if (shouldRenderParkingHeatmap) {
      addParkingHeatmapSource();
      if (!map.getLayer(parkingsV2HeatmapLayerId)) {
        map.addLayer({
          id: parkingsV2HeatmapLayerId,
          type: 'heatmap',
          source: parkingsV2HeatmapSourceId,
          paint: {
            'heatmap-weight': ['interpolate', ['linear'], ['coalesce', ['get', 'capacity'], 1], 1, 0.15, 10, 0.35, 50, 0.75, 150, 1],
            'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 8, 0.6, 15, 1.6],
            'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 8, 12, 15, 30],
            'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 8, 0.75, 16, 0.95],
            'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgb(0 179 222 / 0)', 0.1, '#00b3de', 0.25, '#3b53a3', 0.4, '#19c570', 0.6, '#c2ae2f', 0.9, '#c23b2f', 1, 'rgb(170 0 255)']
          },
          filter: getParkingHeatmapFilter()
        }, map.getLayer('labels') ? 'labels' : undefined);
      }
      updateParkingHeatmapFilter();
    } else {
      addCircleLayer(activeParkingsLayerId, activeParkingsSourceId, {
        'circle-radius': ['get', 'radius'],
        'circle-color': ['get', 'color']
      }, shouldClusterParkings ? {
        filter: ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'secure'], true]]
      } : undefined);
      if (shouldClusterParkings) {
        addCircleLayer(parkingsV2SecurePointsLayerId, parkingsV2SecureClusterSourceId, {
          'circle-radius': ['get', 'radius'],
          'circle-color': ['get', 'color']
        }, {
          filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'secure'], true]]
        });
      }
    }
    if (!shouldRenderParkingHeatmap) {
      getHighlightedParkingLayers().forEach(({
        layerId,
        sourceId
      }) => {
        if (map.getLayer(layerId)) return;
        map.addLayer({
          id: layerId,
          type: 'circle',
          source: sourceId,
          paint: {
            'circle-color': 'transparent',
            'circle-radius': ['+', ['coalesce', ['get', 'radius'], 10], 2],
            'circle-stroke-color': '#111827',
            'circle-stroke-width': 3
          },
          filter: getHighlightedParkingFilter([], null)
        });
      });
    }
    moveHighlightedParkingLayerAbovePoints();
    moveParkingClusterLayersAboveZones();
  }
  function getHighlightedParkingLayers() {
    if (!shouldClusterParkings) return [{
      layerId: highlightedParkingLayerId,
      sourceId: activeParkingsSourceId,
      baseFilters: []
    }];
    return [{
      layerId: highlightedParkingLayerId,
      sourceId: activeParkingsSourceId,
      baseFilters: [['!', ['has', 'point_count']], ['!=', ['get', 'secure'], true]]
    }, {
      layerId: highlightedSecureParkingLayerId,
      sourceId: parkingsV2SecureClusterSourceId,
      baseFilters: [['!', ['has', 'point_count']], ['==', ['get', 'secure'], true]]
    }];
  }
  function getHighlightedParkingFilter(baseFilters, parkingId) {
    if (parkingId === null || parkingId === undefined) return ['==', ['literal', true], false];
    return ['all', ...baseFilters, ['==', ['get', 'id'], parkingId]];
  }
  function getParkingHeatmapFilter() {
    return parkingHeatmapGeometry ? ['within', parkingHeatmapGeometry] : ['==', ['literal', true], false];
  }
  function updateParkingHeatmapFilter() {
    if (!map || !map.getLayer(parkingsV2HeatmapLayerId)) return;
    map.setFilter(parkingsV2HeatmapLayerId, getParkingHeatmapFilter());
  }
  function registerMapHandlers() {
    if (!map) return;
    map.on('mousemove', cyclabilityZonesLayerId, handleMoveInZone);
    map.on('click', cyclabilityZonesLayerId, handleClickZone);
    if (hasParkingPointInteractions) {
      map.on('click', activeParkingsLayerId, handleClickParking);
      map.on('mousemove', activeParkingsLayerId, handleMoveInParking);
      map.on('mouseleave', activeParkingsLayerId, handleLeaveParking);
      if (shouldClusterParkings) {
        map.on('click', parkingsV2SecurePointsLayerId, handleClickParking);
        map.on('mousemove', parkingsV2SecurePointsLayerId, handleMoveInParking);
        map.on('mouseleave', parkingsV2SecurePointsLayerId, handleLeaveParking);
      }
    }
    if (shouldClusterParkings) {
      parkingsV2ClusterLayerIds.forEach(layerId => {
        map.on('click', layerId, handleClickParkingCluster);
        map.on('mousemove', layerId, handleMoveInParkingCluster);
        map.on('mouseenter', layerId, handleEnterParkingCluster);
        map.on('mouseleave', layerId, handleLeaveParkingCluster);
      });
      parkingsV2ClusterCountLayerIds.forEach(layerId => {
        map.on('click', layerId, handleClickParkingCluster);
      });
    }
    map.on('mouseleave', cyclabilityZonesLayerId, handleLeaveZone);
    map.on('moveend', handleMoveEnd);
    map.on('click', handleClickMap);
  }
  function unregisterMapHandlers() {
    if (!map) return;
    map.off('mousemove', cyclabilityZonesLayerId, handleMoveInZone);
    map.off('click', cyclabilityZonesLayerId, handleClickZone);
    map.off('mouseleave', cyclabilityZonesLayerId, handleLeaveZone);
    map.off('moveend', handleMoveEnd);
    map.off('click', handleClickMap);
    if (hasParkingPointInteractions) {
      map.off('click', activeParkingsLayerId, handleClickParking);
      map.off('mousemove', activeParkingsLayerId, handleMoveInParking);
      map.off('mouseleave', activeParkingsLayerId, handleLeaveParking);
      if (shouldClusterParkings) {
        map.off('click', parkingsV2SecurePointsLayerId, handleClickParking);
        map.off('mousemove', parkingsV2SecurePointsLayerId, handleMoveInParking);
        map.off('mouseleave', parkingsV2SecurePointsLayerId, handleLeaveParking);
      }
    }
    if (shouldClusterParkings) {
      parkingsV2ClusterLayerIds.forEach(layerId => {
        map.off('click', layerId, handleClickParkingCluster);
        map.off('mousemove', layerId, handleMoveInParkingCluster);
        map.off('mouseenter', layerId, handleEnterParkingCluster);
        map.off('mouseleave', layerId, handleLeaveParkingCluster);
      });
      parkingsV2ClusterCountLayerIds.forEach(layerId => {
        map.off('click', layerId, handleClickParkingCluster);
      });
    }
  }
  function handleMoveInZone({
    lngLat,
    features
  }) {
    if (map && features && features.length > 0 && map.getZoom() < 15) {
      const {
        id,
        geometry,
        properties
      } = features[0];
      const hoveredId = typeof id === 'number' ? id : typeof id === 'string' ? Number(id) : NaN;
      if (!Number.isNaN(hoveredId) && hoveredId === selectedZoneIdRef.current) {
        highlightedZoneTooltip.current?.remove();
        return;
      }
      highlightedZoneTooltip.current?.setHTML(getTooltipContent?.(properties) || '').setLngLat(lngLat).addTo(map);
      if (hoveredZoneId !== id) {
        const highlightedZoneSource = getHighlightedZoneSource();
        if (!highlightedZoneSource) return;
        hoveredZoneId = id;
        if (hoveredZoneId) {
          map.getCanvas().style.cursor = 'pointer';
          highlightedZoneSource.setData({
            type: 'Feature',
            geometry,
            properties: {}
          });
        } else {
          clearZoneHighlight();
        }
      }
    }
  }
  function handleClickZone(event) {
    if (hasParkingAtPoint(event)) return;
    if (hasParkingClusterAtPoint(event)) return;
    const {
      features
    } = event;
    if (features && features.length > 0) {
      const {
        id,
        properties
      } = features[0];
      const rawZoneId = typeof id === 'number' || typeof id === 'string' ? id : properties?.id;
      const zoneId = typeof rawZoneId === 'number' ? rawZoneId : typeof rawZoneId === 'string' ? Number(rawZoneId) : NaN;
      if (!Number.isNaN(zoneId)) {
        const zone = zoneMap.current[zoneId];
        if (zone) onClick?.(zone);
      }
    }
  }
  function handleLeaveZone() {
    if (!map) return;
    map.getCanvas().style.cursor = '';
    clearZoneHighlight();
  }
  function handleMoveEnd() {
    if (!map || map.getZoom() < 15) return;
    map.getCanvas().style.cursor = '';
    clearZoneHighlight();
  }
  function handleClickMap(event) {
    if (!map || !parkingHoverPanelPinned.current) return;
    if (hasParkingAtPoint(event)) return;
    clearParkingsHighlight();
  }
  function handleClickParking({
    features,
    originalEvent
  }) {
    if (!map || !features || features.length === 0) return;
    const {
      geometry,
      properties
    } = features[0];
    if (geometry.type !== 'Point' || !properties) return;
    originalEvent.preventDefault();
    originalEvent.stopPropagation();
    const useV2ParkingDetails = parkingDetailsMode === 'v2';
    parkingHoverPanelPinned.current = useV2ParkingDetails && Boolean(parkingHoverPanelRef?.current);
    hoveredParkingId = properties.id;
    if (useV2ParkingDetails && parkingHoverPanelRef?.current) {
      highlightParkingFeature(properties);
      renderParkingHoverPanel(properties, geometry, {
        pinned: true
      });
    } else {
      highlightedParkingTooltip.current?.setHTML(useV2ParkingDetails ? getParkingPopupContent(properties) : getLegacyParkingPopupContent(properties)).setLngLat({
        lat: geometry.coordinates[1],
        lng: geometry.coordinates[0]
      }).addTo(map);
    }
  }
  function handleMoveInParking({
    features
  }) {
    if (!map || !features || features.length === 0) return;
    const {
      geometry,
      properties
    } = features[0];
    if (geometry.type !== 'Point' || !properties) return;
    if (parkingHoverPanelPinned.current) {
      map.getCanvas().style.cursor = 'pointer';
      return;
    }
    const parkingId = properties.id;
    const coordinates = {
      lat: geometry.coordinates[1],
      lng: geometry.coordinates[0]
    };
    if (hoveredParkingId !== parkingId) {
      hoveredParkingId = parkingId;
      const useV2ParkingDetails = parkingDetailsMode === 'v2';
      if (useV2ParkingDetails && parkingHoverPanelRef?.current) {
        highlightParkingFeature(properties);
        renderParkingHoverPanel(properties, geometry);
      } else {
        highlightedParkingTooltip.current?.setHTML(useV2ParkingDetails ? getParkingPopupContent(properties) : getLegacyParkingPopupContent(properties)).setLngLat(coordinates).addTo(map);
      }
      if (useV2ParkingDetails) {
        parkingPanoramaxKey.current = geometry.coordinates.join(',');
      }
    } else if (!parkingHoverPanelRef?.current) {
      highlightedParkingTooltip.current?.setLngLat(coordinates);
    }
    if (hoveredParkingId) {
      map.getCanvas().style.cursor = 'pointer';
    } else {
      clearParkingsHighlight();
    }
  }
  function handleLeaveParking() {
    if (!map) return;
    map.getCanvas().style.cursor = '';
    if (parkingHoverPanelPinned.current) return;
    if (parkingHoverPanelRef?.current) scheduleParkingHoverPanelClose();else clearParkingsHighlight();
  }
  function handleMoveInParkingCluster() {
    if (!map) return;
    if (parkingHoverPanelPinned.current) return;
    cancelPromises();
    highlightedParkingTooltip.current?.remove();
  }
  function handleEnterParkingCluster() {
    if (!map) return;
    map.getCanvas().style.cursor = 'pointer';
  }
  function handleLeaveParkingCluster() {
    if (!map) return;
    map.getCanvas().style.cursor = '';
    if (parkingHoverPanelPinned.current) return;
    clearParkingsHighlight();
  }
  function formatBoolean(value) {
    return value ? t('cycling-insights.cartographic_data.parkings.popup.yes') : t('cycling-insights.cartographic_data.parkings.popup.no');
  }
  function formatTypology(properties) {
    return [properties?.secure ? t('commons.parking_types.secure') : properties?.type === 'sheltered' ? t('commons.parking_types.sheltered') : '', properties?.rackType === 'stands' ? t('commons.parking_types.arches') : properties?.rackType === 'wallLoops' ? t('commons.parking_types.racks') : properties?.rackType === 'twoTier' ? t('commons.parking_types.two_tier') : ''].filter(Boolean).join(' • ');
  }
  function getParkingPopupContent(properties) {
    const rows = [[t('cycling-insights.cartographic_data.parkings.popup.operator'), properties?.operator || t('cycling-insights.cartographic_data.parkings.popup.unknown_operator')], [t('cycling-insights.cartographic_data.parkings.popup.network'), properties?.network || t('cycling-insights.cartographic_data.parkings.popup.unknown_network')], [t('cycling-insights.cartographic_data.parkings.popup.typology'), formatTypology(properties)], [t('cycling-insights.cartographic_data.parkings.popup.capacity'), t('commons.parking.racks', {
      count: properties?.capacity || 0,
      capacity: properties?.capacity || 0,
      context: !properties?.capacity ? 'zero_long' : ''
    })], [t('cycling-insights.cartographic_data.parkings.popup.protection'), formatBoolean(Boolean(properties?.secure))], [t('cycling-insights.cartographic_data.parkings.popup.free'), formatBoolean(Boolean(properties?.free))], [t('cycling-insights.cartographic_data.parkings.popup.covered'), formatBoolean(Boolean(properties?.covered))], properties?.startDate ? [t('cycling-insights.cartographic_data.parkings.popup.start_date'), properties.startDate] : null].filter(row => Boolean(row && row[1]));
    const osmUrl = getParkingOsmUrl(properties);
    return getParkingPanelContent({
      eyebrow: 'Stationnement',
      osmUrl,
      rows,
      title: t('cycling-insights.cartographic_data.parkings.popup.title')
    });
  }
  function getParkingPanelContent({
    eyebrow,
    osmUrl,
    rows,
    title
  }) {
    const informationRows = rows.map(([label, value]) => `
          <div style="align-items:start;border-bottom:1px solid #e2e8f0;display:grid;gap:12px;grid-template-columns:minmax(92px,0.9fr) minmax(0,1.1fr);padding:10px 0;">
            <div style="color:#64748b;font-size:12px;font-weight:700;line-height:1.3;text-transform:uppercase;">
              ${escapeHtml(label.replace(/ :$/, '').replace(/:$/, ''))}
            </div>
            <div style="color:#0f172a;font-size:14px;font-weight:650;line-height:1.35;text-align:right;">
              ${escapeHtml(value)}
            </div>
          </div>
        `).join('');
    const osmLink = osmUrl ? `
        <a
          href="${escapeHtml(osmUrl)}"
          rel="noreferrer"
          style="align-items:center;color:#2563eb;display:inline-flex;font-size:13px;font-weight:650;text-decoration:none;"
          target="_blank"
        >
          Voir sur OpenStreetMap
        </a>
      ` : '';
    return `
      <div style="display:flex;flex-direction:column;gap:16px;">
        <header style="align-items:center;display:flex;gap:12px;">
          <div>
            <div style="color:#64748b;font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;">
              ${escapeHtml(eyebrow)}
            </div>
            <h3 style="color:#0f172a;font-size:20px;line-height:1.15;margin:4px 0 0;">
              ${escapeHtml(title)}
            </h3>
          </div>
        </header>
        <section style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:14px;padding:2px 14px;">
          ${informationRows}
        </section>
        <footer style="border-top:1px solid #e2e8f0;display:flex;flex-direction:column;gap:10px;padding-top:16px;">
          ${osmLink}
        </footer>
      </div>
    `;
  }
  function getParkingTypeIcon(properties) {
    if (properties?.secure) return parkingTypes.supervised.icon;
    const type = properties?.type;
    if (typeof type === 'string' && type in parkingTypes) {
      return parkingTypes[type].icon;
    }
    return null;
  }
  function getParkingTypologyContent(properties) {
    const items = getParkingTypologyItems(properties);
    return /*#__PURE__*/createElement('span', {
      style: {
        alignItems: 'flex-end',
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 4
      }
    }, items.map(({
      icon,
      label
    }) => /*#__PURE__*/createElement('span', {
      key: label,
      style: {
        alignItems: 'center',
        display: 'inline-flex',
        gap: 6,
        justifyContent: 'flex-end'
      }
    }, /*#__PURE__*/createElement('img', {
      alt: '',
      height: 16,
      src: icon,
      width: 16
    }), /*#__PURE__*/createElement('span', null, label))));
  }
  function getParkingTypologyItems(properties) {
    return [getParkingShelterTypologyItem(properties), getParkingRackTypologyItem(properties)].filter(item => Boolean(item));
  }
  function getParkingShelterTypologyItem(properties) {
    const icon = getParkingTypeIcon(properties);
    if (!icon) return null;
    const label = properties?.secure ? t('commons.parking_types.secure') : properties?.type === 'sheltered' ? t('commons.parking_types.sheltered') : '';
    return label ? {
      icon,
      label
    } : null;
  }
  function getParkingRackTypologyItem(properties) {
    if (properties?.rackType === 'stands') {
      return {
        icon: standsIcon,
        label: t('commons.parking_types.arches')
      };
    }
    if (properties?.rackType === 'wallLoops') {
      return {
        icon: wallLoopIcon,
        label: t('commons.parking_types.racks')
      };
    }
    if (properties?.rackType === 'twoTier') {
      return {
        icon: parkingTypes.simple.icon,
        label: t('commons.parking_types.two_tier')
      };
    }
    return null;
  }
  function getLegacyParkingPopupContent(properties) {
    return `<h3>${[formatTypology(properties), properties?.free ? t('commons.parking_types.free') : ''].filter(Boolean).join(' • ')}</h3><span>${t('commons.parking.racks', {
      count: properties?.capacity || 0,
      capacity: properties?.capacity || 0,
      context: !properties?.capacity ? 'zero_long' : ''
    })}</span>`;
  }
  function getParkingOsmUrl(properties) {
    const osmId = properties?.osmId;
    const osmGeom = properties?.osmGeom;
    if (!osmId) return null;
    if (osmGeom !== 'node' && osmGeom !== 'way') {
      return `https://www.openstreetmap.org/node/${osmId}`;
    }
    return `https://www.openstreetmap.org/${osmGeom}/${osmId}`;
  }
  function renderParkingHoverPanel(properties, geometry, {
    pinned = false
  } = {}) {
    if (!parkingHoverPanelRef?.current) return;
    parkingHoverPanelPinned.current = pinned;
    clearParkingHoverPanelCloseTimeout();
    highlightedParkingTooltip.current?.remove();
    cancelPromises();
    renderParkingDetailsPanel(properties, geometry);
    parkingHoverPanelRef.current.onmouseenter = () => {
      parkingHoverPanelHovered.current = true;
      clearParkingHoverPanelCloseTimeout();
    };
    parkingHoverPanelRef.current.onmouseleave = () => {
      parkingHoverPanelHovered.current = false;
      if (!parkingHoverPanelPinned.current) clearParkingsHighlight();
    };
    parkingHoverPanelRef.current.style.display = 'block';
    parkingHoverPanelPositionRef?.current?.();
  }
  function hideParkingHoverPanel() {
    if (!parkingHoverPanelRef?.current) return;
    clearParkingDetailsPanel();
    parkingHoverPanelRef.current.onmouseenter = null;
    parkingHoverPanelRef.current.onmouseleave = null;
    parkingHoverPanelRef.current.style.display = 'none';
  }
  function scheduleParkingHoverPanelClose() {
    clearParkingHoverPanelCloseTimeout();
    parkingHoverPanelCloseTimeout.current = window.setTimeout(() => {
      if (!parkingHoverPanelHovered.current) clearParkingsHighlight();
    }, parkingHoverPanelCloseDelay);
  }
  function clearParkingHoverPanelCloseTimeout() {
    if (parkingHoverPanelCloseTimeout.current !== undefined) {
      window.clearTimeout(parkingHoverPanelCloseTimeout.current);
      parkingHoverPanelCloseTimeout.current = undefined;
    }
  }
  function getClusterSummary(count, activeCount, activeLabel, inactiveLabel) {
    if (activeCount === 0) return inactiveLabel;
    if (activeCount === count) return activeLabel;
    return t('cycling-insights.cartographic_data.parkings.popup.mixed');
  }
  function getParkingRows(properties) {
    return [[t('cycling-insights.cartographic_data.parkings.popup.operator'), properties?.operator || t('cycling-insights.cartographic_data.parkings.popup.unknown_operator')], [t('cycling-insights.cartographic_data.parkings.popup.network'), properties?.network || t('cycling-insights.cartographic_data.parkings.popup.unknown_network')], [t('cycling-insights.cartographic_data.parkings.popup.typology'), getParkingTypologyContent(properties)], [t('cycling-insights.cartographic_data.parkings.popup.capacity'), t('commons.parking.racks', {
      count: properties?.capacity || 0,
      capacity: properties?.capacity || 0,
      context: !properties?.capacity ? 'zero_long' : ''
    })], [t('cycling-insights.cartographic_data.parkings.popup.protection'), formatBoolean(Boolean(properties?.secure))], [t('cycling-insights.cartographic_data.parkings.popup.free'), formatBoolean(Boolean(properties?.free))], [t('cycling-insights.cartographic_data.parkings.popup.covered'), formatBoolean(Boolean(properties?.covered))], properties?.startDate ? [t('cycling-insights.cartographic_data.parkings.popup.start_date'), properties.startDate] : null].filter(row => Boolean(row && row[1]));
  }
  function getParkingClusterRows(properties) {
    const count = Number(properties?.point_count || 0);
    const capacity = Math.round(Number(properties?.capacitySum || 0));
    const secureCount = Number(properties?.secureCount || 0);
    const freeCount = Number(properties?.freeCount || 0);
    const coveredCount = Number(properties?.coveredCount || 0);
    const operatorCount = Number(properties?.operatorCount || 0);
    return [[t('cycling-insights.cartographic_data.parkings.popup.operator'), getClusterSummary(count, operatorCount, t('cycling-insights.cartographic_data.parkings.popup.cluster_known_operator'), t('cycling-insights.cartographic_data.parkings.popup.cluster_unknown_operator'))], [t('cycling-insights.cartographic_data.parkings.popup.typology'), t('cycling-insights.cartographic_data.parkings.popup.cluster_typology', {
      count
    })], [t('cycling-insights.cartographic_data.parkings.popup.capacity'), t('commons.parking.racks', {
      count: capacity,
      capacity
    })], [t('cycling-insights.cartographic_data.parkings.popup.protection'), getClusterSummary(count, secureCount, formatBoolean(true), formatBoolean(false))], [t('cycling-insights.cartographic_data.parkings.popup.free'), getClusterSummary(count, freeCount, formatBoolean(true), formatBoolean(false))], [t('cycling-insights.cartographic_data.parkings.popup.covered'), getClusterSummary(count, coveredCount, formatBoolean(true), formatBoolean(false))]];
  }
  function renderParkingDetailsPanel(properties, geometry) {
    if (!parkingHoverPanelRef?.current) return;
    if (!parkingDetailsRoot.current) {
      parkingDetailsRoot.current = createRoot(parkingHoverPanelRef.current);
    }
    const isCluster = !geometry;
    const osmUrl = isCluster ? null : getParkingOsmUrl(properties);
    parkingDetailsRoot.current.render(/*#__PURE__*/createElement(ParkingDetailsPanel, {
      contribution: geometry && onParkingContributionSubmitRef.current ? {
        addPhotoLabel: t('cycling-insights.cartographic_data.parkings.popup.contribution.add_photo'),
        buttonLabel: t('cycling-insights.cartographic_data.parkings.popup.contribution.button'),
        cancelLabel: t('commons.actions.cancel'),
        descriptionLabel: t('cycling-insights.cartographic_data.parkings.popup.contribution.description_label'),
        descriptionRequiredError: t('cycling-insights.cartographic_data.parkings.popup.contribution.description_required'),
        photosLabel: t('cycling-insights.cartographic_data.parkings.popup.contribution.photos_label'),
        removePhotoLabel: t('cycling-insights.cartographic_data.parkings.popup.contribution.remove_photo'),
        submitLabel: t('commons.actions.send'),
        onSubmit: (description, photos) => onParkingContributionSubmitRef.current?.({
          description,
          osmUrl,
          parking: properties,
          photos,
          point: geometry
        }) || Promise.resolve()
      } : undefined,
      eyebrow: isCluster ? t('cycling-insights.cartographic_data.parkings.popup.cluster_eyebrow') : t('cycling-insights.cartographic_data.parkings.popup.eyebrow'),
      icon: isCluster ? null : getParkingTypeIcon(properties),
      loadPanoramaxPreview: geometry && !isCluster ? () => getParkingPanoramaxPreview(geometry.coordinates) : undefined,
      onAction: action => onParkingDetailsActionRef.current?.({
        action,
        parking: properties
      }),
      osmLabel: t('cycling-insights.cartographic_data.parkings.popup.open_osm'),
      osmUrl,
      rows: (isCluster ? getParkingClusterRows(properties) : getParkingRows(properties)).map(([label, value]) => ({
        label,
        value
      })),
      title: isCluster ? t('cycling-insights.cartographic_data.parkings.popup.cluster_title') : t('cycling-insights.cartographic_data.parkings.popup.title')
    }));
  }
  function clearParkingDetailsPanel() {
    parkingDetailsRoot.current?.unmount();
    parkingDetailsRoot.current = null;
  }
  function moveParkingClusterLayersAboveZones() {
    if (!map || !shouldClusterParkings) return;
    const beforeLayerId = map.getLayer('labels') ? 'labels' : undefined;
    [parkingsV2OtherClusterLayerId, parkingsV2OtherClusterCountLayerId, activeParkingsLayerId, parkingsV2SecurePointsLayerId, parkingsV2SecureClusterLayerId, parkingsV2SecureClusterCountLayerId, highlightedParkingLayerId, highlightedSecureParkingLayerId].forEach(layerId => {
      if (map.getLayer(layerId)) map.moveLayer(layerId, beforeLayerId);
    });
  }
  function moveHighlightedParkingLayerAbovePoints() {
    if (!map) return;
    const beforeLayerId = map.getLayer('labels') ? 'labels' : undefined;
    [highlightedParkingLayerId, highlightedSecureParkingLayerId].forEach(layerId => {
      if (map.getLayer(layerId)) map.moveLayer(layerId, beforeLayerId);
    });
  }
  function hasParkingClusterAtPoint({
    point
  }) {
    if (!map || !shouldClusterParkings) return false;
    return map.queryRenderedFeatures(point, {
      layers: [...parkingsV2ClusterLayerIds, ...parkingsV2ClusterCountLayerIds].filter(layerId => map.getLayer(layerId))
    }).length > 0;
  }
  function hasParkingAtPoint({
    point
  }) {
    if (!map || !map.getLayer(activeParkingsLayerId)) return false;
    const parkingPointLayerIds = [activeParkingsLayerId, parkingsV2SecurePointsLayerId].filter(layerId => map.getLayer(layerId));
    return map.queryRenderedFeatures(point, {
      layers: parkingPointLayerIds
    }).length > 0;
  }
  function handleClickParkingCluster({
    features,
    originalEvent
  }) {
    if (!map) return;
    const feature = features?.[0];
    if (!feature || feature.geometry.type !== 'Point') return;
    originalEvent.preventDefault();
    originalEvent.stopPropagation();
    map.flyTo({
      center: {
        lng: feature.geometry.coordinates[0],
        lat: feature.geometry.coordinates[1]
      },
      zoom: Math.ceil(map.getZoom() + 1)
    });
  }
  function update(zone, childZones, selectedStatsKey, parkings) {
    if (parkings && zone === null && childZones === null && shouldRenderParkingHeatmap) {
      updateParkings(null, parkings);
      lastRenderedChildZones.current = null;
      return;
    }
    if (childZones) {
      const selectedZoneId = zone?.id || null;
      const childZonesKey = [...childZones].sort((a, b) => a.id - b.id).map(({
        id,
        stats
      }) => `${id}:${stats[0]?.dateAsKey ?? ''}`).join(',');
      const hasSameChildZonesAsPreviousRender = lastRenderedChildZones.current?.key === childZonesKey && lastRenderedChildZones.current.selectedStatsKey === selectedStatsKey && lastRenderedChildZones.current.selectedZoneId === selectedZoneId;
      if (hasSameChildZonesAsPreviousRender) {
        if (zone?.geometry) {
          updateCyclabilityZoneSource({
            type: 'FeatureCollection',
            features: [{
              id: zone.id,
              type: 'Feature',
              geometry: zone.geometry,
              properties: {
                id: zone.id
              }
            }]
          });
        } else {
          clearCyclabilityZoneSource();
        }
        if (parkings && (zone || shouldRenderParkingHeatmap)) updateParkings(zone, parkings);else if (parkings && !shouldClusterParkings) clearParkingSources();
        return;
      }
      clear(true, parkings === undefined);
      const maxDistance = childZones.reduce((res, {
        stats
      }) => {
        const dateStats = stats.find(({
          dateAsKey
        }) => dateAsKey === selectedStatsKey);
        if (dateStats) {
          return Math.max(res, dateStats.distances.all);
        }
        return res;
      }, -1);
      const maxSlots = childZones.reduce((res, {
        statsBNSC
      }) => {
        const dateStatsBNSC = statsBNSC.find(({
          dateAsKey
        }) => dateAsKey === selectedStatsKey);
        if (dateStatsBNSC) {
          return Math.max(res, dateStatsBNSC.slots.all);
        }
        return res;
      }, -1);
      updateCyclabilityZonesSource({
        type: 'FeatureCollection',
        features: childZones.reduce((res, {
          id,
          name,
          geometry,
          stats,
          statsBNSC
        }) => {
          const dateStats = stats.find(({
            dateAsKey
          }) => dateAsKey === selectedStatsKey);
          const dateStatsBNSC = statsBNSC.find(({
            dateAsKey
          }) => dateAsKey === selectedStatsKey);
          const distance = dateStats?.distances.all || 0;
          const slots = dateStatsBNSC?.slots.all || 0;
          const colorIndex = isParkings ? Math.round(slots / maxSlots * (cyclabilityZonesColors.length - 1)) : Math.round(distance / maxDistance * (cyclabilityZonesColors.length - 1));
          const isSelectedZone = Boolean(zone && id === zone.id);
          const fillOpacity = zone && unselectedChildZoneOpacity !== undefined ? isSelectedZone ? 0 : unselectedChildZoneOpacity : 0.5;
          if (geometry) {
            res.push({
              id,
              type: 'Feature',
              geometry,
              properties: {
                id,
                name,
                distance,
                capacite: slots,
                fillOpacity,
                color: isParkings ? slots > 0 ? cyclabilityZonesColors[colorIndex] : '#6c6c6c' : distance > 0 ? cyclabilityZonesColors[colorIndex] : '#6c6c6c'
              }
            });
          }
          return res;
        }, [])
      });
      if (isParkings && childZones.length > 1) {
        updateCyclabilityZonesParkingLotsCountSource({
          type: 'FeatureCollection',
          features: childZones.reduce((res, {
            id,
            geometry,
            statsBNSC
          }) => {
            if (zone?.administrativeLevel === 'city' && id === zone.id) return res;
            const dateStatsBNSC = statsBNSC.find(({
              dateAsKey
            }) => dateAsKey === selectedStatsKey);
            const slots = dateStatsBNSC?.slots.all || 0;
            if (slots > 0 && geometry && geometry.coordinates.length > 0) {
              res.push({
                id,
                type: 'Feature',
                geometry: centroid(geometry).geometry,
                properties: {
                  id,
                  count: formatNumber(slots),
                  radius: getParkingZoneCountRadius(slots, maxSlots)
                }
              });
            }
            return res;
          }, [])
        });
      }
      if (zone?.geometry) {
        updateCyclabilityZoneSource({
          type: 'FeatureCollection',
          features: [{
            id: zone.id,
            type: 'Feature',
            geometry: zone.geometry,
            properties: {
              id: zone.id
            }
          }]
        });
      }
      if (parkings && (zone || shouldRenderParkingHeatmap)) updateParkings(zone, parkings);
      lastRenderedChildZones.current = {
        key: childZonesKey,
        selectedStatsKey,
        selectedZoneId
      };
    } else if (parkings && zone) {
      clear(true, true);
      const {
        id,
        geometry
      } = zone;
      if (!geometry) return;
      updateCyclabilityZoneSource({
        type: 'FeatureCollection',
        features: [{
          id,
          type: 'Feature',
          geometry,
          properties: {
            id
          }
        }]
      });
      updateParkings(zone, parkings);
    } else if (zone) {
      clear(true, true);
      const {
        id,
        geometry
      } = zone;
      if (!geometry) return;
      updateCyclabilityZoneSource({
        type: 'FeatureCollection',
        features: [{
          id,
          type: 'Feature',
          geometry,
          properties: {
            id
          }
        }]
      });
      lastRenderedChildZones.current = null;
    } else {
      clear(true, true);
      lastRenderedChildZones.current = null;
    }
  }
  function updateParkings(zone, parkings) {
    if (!zone?.geometry && !shouldRenderParkingHeatmap) return;
    const radiuses = isParkingV2RenderMode ? parkingRadiuses : legacyParkingRadiuses;
    let minCapacity = Infinity;
    let maxCapacity = 0;
    parkings.forEach(({
      capacity
    }) => {
      minCapacity = Math.min(minCapacity, capacity || 0);
      maxCapacity = Math.max(maxCapacity, capacity || 0);
    });
    const features = parkings.map(parking => {
      const {
        id,
        location,
        capacity,
        rackType,
        free,
        sheltered,
        type,
        startDate,
        osmId,
        osmGeom,
        operator
      } = parking;
      let radiusIndex;
      if (minCapacity === maxCapacity) radiusIndex = Math.round(radiuses.length / 2) - 1;else {
        const percent = ((capacity || 0) - minCapacity) / (maxCapacity - minCapacity);
        radiusIndex = Math.round(percent * (radiuses.length - 1));
      }
      const secure = isParkingSecure(parking);
      const operated = isParkingOperated(parking);
      return {
        id,
        type: 'Feature',
        geometry: location.point,
        properties: {
          id,
          covered: sheltered,
          free,
          network: getParkingNetworkLabel(parking),
          operated,
          operator,
          osmGeom,
          osmId,
          rackType,
          secure,
          startDate,
          type,
          capacity,
          color: getParkingPointColor(parking),
          radius: radiuses[radiusIndex !== null ? radiusIndex : Math.floor(radiuses.length / 2)].value
        }
      };
    });
    const featureCollection = {
      type: 'FeatureCollection',
      features
    };
    if (shouldRenderParkingHeatmap) updateParkingHeatmapSource(featureCollection);else updateParkingsSource(featureCollection);
    if (shouldClusterParkings) {
      const secureFeatures = features.filter(({
        properties
      }) => properties?.secure);
      updateOtherClusterParkingsSource({
        type: 'FeatureCollection',
        features: features.filter(({
          properties
        }) => !properties?.secure)
      });
      updateSecureClusterParkingsSource({
        type: 'FeatureCollection',
        features: secureFeatures
      });
    }
  }
  function displayCurrentArea({
    geometry
  }) {
    if (geometry) updateAreaSource({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry,
        properties: {}
      }]
    });
  }
  async function getParkingPanoramaxPreview(coordinates) {
    const [lng, lat] = coordinates;
    if (typeof lng !== 'number' || typeof lat !== 'number') return null;
    cancelPromises();
    const parkingCoordinates = [lng, lat];
    const key = parkingCoordinates.join(',');
    parkingPanoramaxKey.current = key;
    const cached = parkingPanoramaxCache.current.get(key);
    if (cached !== undefined) {
      return cached ? formatParkingPanoramaxPreview(cached) : null;
    }
    const now = new Date();
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - 2);
    const panoramaxBboxHalfSize = 0.0003;
    const bbox = [lng - panoramaxBboxHalfSize, lat - panoramaxBboxHalfSize, lng + panoramaxBboxHalfSize, lat + panoramaxBboxHalfSize];
    try {
      const preview = await cancellablePromise(PanoramaxService.getLatestPreview({
        bbox,
        from: cutoff,
        to: now,
        coordinates: parkingCoordinates
      }));
      parkingPanoramaxCache.current.set(key, preview);
      if (preview && parkingPanoramaxKey.current === key) {
        return formatParkingPanoramaxPreview(preview);
      }
    } catch (err) {
      if (!(err instanceof Error) || err.name !== 'CancelledPromiseError') {
        parkingPanoramaxCache.current.set(key, null);
      }
    }
    return null;
  }
  function formatParkingPanoramaxPreview(preview) {
    const formattedDate = new Intl.DateTimeFormat(i18n.language, {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(new Date(preview.datetime));
    return {
      formattedDate,
      linkUrl: preview.linkUrl || PanoramaxService.getViewerUrl(preview.coordinates),
      thumbnailUrl: preview.thumbnailUrl
    };
  }
  function clearZoneHighlight() {
    highlightedZoneTooltip.current?.remove();
    const highlightedZoneSource = getHighlightedZoneSource();
    if (highlightedZoneSource) {
      highlightedZoneSource.setData({
        type: 'FeatureCollection',
        features: []
      });
    }
    hoveredZoneId = undefined;
  }
  function highlightParkingFeature(properties) {
    if (!map) return;
    getHighlightedParkingLayers().forEach(({
      layerId,
      baseFilters
    }) => {
      if (!map.getLayer(layerId)) return;
      map.setFilter(layerId, getHighlightedParkingFilter(baseFilters, properties?.id ?? null));
    });
  }
  function clearHighlightedParking() {
    if (!map) return;
    getHighlightedParkingLayers().forEach(({
      layerId
    }) => {
      if (!map.getLayer(layerId)) return;
      map.setFilter(layerId, getHighlightedParkingFilter([], null));
    });
  }
  function clearParkingsHighlight() {
    clearParkingHoverPanelCloseTimeout();
    highlightedParkingTooltip.current?.remove();
    clearHighlightedParking();
    cancelPromises();
    hideParkingHoverPanel();
    hoveredParkingId = undefined;
    parkingPanoramaxKey.current = null;
    parkingHoverPanelHovered.current = false;
    parkingHoverPanelPinned.current = false;
  }
  function clearZones() {
    clearCyclabilityZonesSource();
    clearCyclabilityZonesParkingLotsCountSource();
    clearCyclabilityZoneSource();
    clearZoneHighlight();
    lastRenderedChildZones.current = null;
  }
  function clear(keepArea, keepParkings) {
    if (!keepArea) clearAreaSource();
    clearZones();
    if (!keepParkings) {
      clearParkingSources();
      clearParkingsHighlight();
    }
  }
  function clearParkingSources() {
    clearParkingsSource();
    clearHighlightedParking();
    clearOtherClusterParkingsSource();
    clearSecureClusterParkingsSource();
    clearParkingHeatmapSource();
  }
  function destroy() {
    unregisterMapHandlers();
    clear();
    removeParkingLayersAndSources();
    initializedRef.current = false;
    setInitialized(false);
  }
  function removeParkingLayersAndSources() {
    if (!map) return;
    [legacyParkingsV2ClusterCountLayerId, ...parkingsV2ClusterCountLayerIds, ...parkingsV2ClusterLayerIds, parkingsV2HeatmapLayerId, highlightedParkingLayerId, highlightedSecureParkingLayerId, parkingsV2SecurePointsLayerId, parkingsV2LayerId, parkingsLayerId].forEach(layerId => {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
    });
    [parkingsV2HeatmapSourceId, parkingsV2SecureClusterSourceId, parkingsV2OtherClusterSourceId, parkingsV2SourceId, parkingsSourceId].forEach(sourceId => {
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    });
  }
  return {
    initialized,
    init,
    update,
    displayCurrentArea,
    clear,
    clearZones,
    destroy
  };
}